import apiService from './apiService';
import { API_BASE_URL } from '../config/api';
import { authFetch } from './session';

/**
 * Rückruf-Ticket (contract rueckruf-ticket §4): tenant-wide, off by default.
 * `available=false` = switched off for every salon (kill switch on the server).
 */
export interface CallbackTicketSettings {
  enabled: boolean;
  notify_email: string | null;
  available: boolean;
}

export interface VoiceSettings {
  success: boolean;
  voice_model: string;
  agent_name: string | null;
  privacy_policy_url: string | null;
  cross_sell_enabled: boolean;
  greeting_text: string | null;
  farewell_text: string | null;
  /** Kunden-Anrede beider Kanäle (Telefon + WhatsApp): "du" | "sie". */
  salutation?: 'du' | 'sie';
  /** Missing on servers that do not know the Rückruf-Ticket yet. */
  callback_ticket?: CallbackTicketSettings;
  available_voices: string[];
  /** Per field: is the value the company default (true) or set for this agent? */
  inherited?: Record<string, boolean>;
  error?: string;
}

export interface VoiceSettingsUpdate {
  voice_model?: string;
  agent_name?: string;
  privacy_policy_url?: string;
  cross_sell_enabled?: boolean;
  greeting_text?: string;
  farewell_text?: string;
  /** "du" | "sie"; "" setzt auf den Unternehmens-Standard zurück. */
  salutation?: string;
  /** Only the fields sent change; `notify_email: ""` removes the address. Always tenant-wide. */
  callback_ticket?: { enabled?: boolean; notify_email?: string };
}

// Muss eine Live-Stimme sein und zu STANDARD_LIVE im Voice-Server passen
// (voice-server/app/voices.py). Stand bis 14.09.2026 auf 'marin' — eine
// Realtime-Stimme, die seit dem Hardcut gar nicht mehr wählbar ist: das
// Dropdown hätte für jedes Konto ohne eigene Wahl einen Wert vorausgewählt,
// den es in der Liste nicht mehr gibt.
export const DEFAULT_VOICE = 'gleam';

// Die zwölf Stimmen von gpt-live-1 — mehr gibt es nicht mehr.
//
// Seit dem Hardcut vom 13.09.2026 läuft jeder Anruf über die Live-Engine.
// Die zehn Stimmen aus der Realtime-Zeit (marin, cedar, coral, sage, ash,
// echo, verse, shimmer, alloy, ballad) sind deshalb NICHT mehr wählbar; alle
// Bestandskunden wurden auf eine Live-Stimme migriert.
//
// Oben die nordamerikanischen: sie tragen laut OpenAI-Tabelle die neutralste
// regionale Prägung und klingen auf Deutsch am wenigsten fremd. Die übrigen
// haben einen hörbaren Einschlag — das steht bewusst in der Beschreibung,
// damit ein Salon nicht aus Versehen irisch klingt.
//
// Änderungen IMMER zusammen mit AVAILABLE_VOICES im Backend (voice_routes.py)
// und der Registry im Voice-Server (app/voices.py) — drei Tests halten das
// zusammen.
export const VOICE_OPTIONS = [
  { id: 'gleam', name: 'Greta', description: 'Empfohlen, klar und freundlich' },
  { id: 'meridian', name: 'Mats', description: 'Empfohlen, ruhig und sachlich' },
  { id: 'vesper', name: 'Vincent', description: 'Britischer Einschlag, gesetzt' },
  { id: 'willow', name: 'Wilma', description: 'Irischer Einschlag, warm' },
  { id: 'stone', name: 'Stefan', description: 'Irischer Einschlag, ruhig' },
  { id: 'quartz', name: 'Quinn', description: 'Australischer Einschlag, hell' },
  { id: 'ripple', name: 'Robin', description: 'Australischer Einschlag, locker' },
  { id: 'delta', name: 'Dana', description: 'US-Süden, weich' },
  { id: 'cinder', name: 'Cornelius', description: 'US-Süden, tief' },
  { id: 'beacon', name: 'Ben', description: 'Philippinischer Einschlag' },
  { id: 'bossa', name: 'Bianca', description: 'Brasilianischer Einschlag' },
  { id: 'tempo', name: 'Tiago', description: 'Brasilianischer Einschlag' },
];

class VoiceService {
  private static instance: VoiceService;

  private constructor() {}

  public static getInstance(): VoiceService {
    if (!VoiceService.instance) {
      VoiceService.instance = new VoiceService();
    }
    return VoiceService.instance;
  }

  /**
   * Aktuelle Voice-Einstellungen abrufen
   */
  async getSettings(phoneNumber?: string | null): Promise<VoiceSettings> {
    // With an agent number: the values that agent actually uses (its own
    // overrides on top of the company defaults). Without: the defaults.
    const q = phoneNumber ? `?phone_number=${encodeURIComponent(phoneNumber)}` : '';
    return apiService.get<VoiceSettings>(`/voice/settings${q}`);
  }

  /**
   * Voice-Einstellungen aktualisieren
   */
  async updateSettings(
    settings: VoiceSettingsUpdate,
    phoneNumber?: string | null,
  ): Promise<VoiceSettings> {
    // Passing an agent number stores an override for that agent; omitting it
    // writes the company default, exactly as before.
    const body = phoneNumber ? { ...settings, phone_number: phoneNumber } : settings;
    return apiService.put<VoiceSettings>('/voice/settings', body);
  }

  /**
   * Nur Stimme aktualisieren
   */
  async updateVoice(voice_model: string): Promise<VoiceSettings> {
    return this.updateSettings({ voice_model });
  }

  /**
   * Nur Privacy Policy URL aktualisieren
   */
  async updatePrivacyUrl(privacy_policy_url: string): Promise<VoiceSettings> {
    return this.updateSettings({ privacy_policy_url });
  }

  /**
   * MP3-Sample einer Stimme abrufen (Stage 1: "Vorhören"-Button).
   * Gibt ein Blob zurück, das als audio/mpeg abgespielt werden kann.
   */
  async fetchPreviewAudio(voice: string): Promise<Blob> {
    const resp = await authFetch(
      `${API_BASE_URL}/voice/preview?voice=${encodeURIComponent(voice)}`,
    );
    if (!resp.ok) {
      throw new Error(`Preview fetch failed: HTTP ${resp.status}`);
    }
    return resp.blob();
  }

  /**
   * Ticket für das Probegespräch holen.
   *
   * Der Browser spricht NICHT mehr direkt mit OpenAI: seit dem Hardcut läuft
   * jeder Anruf über `gpt-live-1`, und dieses Modell ist vom Browser aus nicht
   * erreichbar (Realtime-Transport: `invalid_model`; Live-API: nimmt weder
   * ephemeren Schlüssel noch Subprotokoll an). Das Gespräch führt deshalb der
   * Voice-Server — derselbe, der auch jeden echten Anruf führt —, und dieses
   * Ticket ist der Ausweis dafür.
   */
  async createProbeTicket(
    params: { voice?: string; phone_number?: string | null } = {},
  ): Promise<ProbeTicket> {
    return apiService.post<ProbeTicket>('/voice/probe-ticket', {
      voice: params.voice,
      phone_number: params.phone_number || undefined,
    });
  }
}

export interface ProbeTicket {
  success: boolean;
  /** Fertige wss-URL inklusive Ticket. Kurzlebig (2 Minuten). */
  ws_url?: string;
  voice?: string;
  business_phone?: string;
  expires_in?: number;
  error?: string;
}

export const voiceService = VoiceService.getInstance();
export default voiceService;
